import SvgPanZoom from 'svg-pan-zoom';
import { pinClustering } from '../../../src/scripts/helpers/pin-clustering';
import { mapMaxZoom } from '../../../src/scripts/helpers/map-zoom';

const classes = {
    elementNode: 'imwa',
    accordionNode: 'accordion',
    mapNode: 'imwa__map',
    mapAnimated: 'imwa__map--is-animated',
    mapImageNode: 'imwa__map__image',
    mapPinNode: 'imwa__map__pin',
    mapZoomInButton: 'imwa__map-zoom-in',
    mapZoomOutButton: 'imwa__map-zoom-out',
    filterOptionsNode: 'imwa__filter-options',
    selectNode: 'imwa__filter-options__select',
};

document.addEventListener('alpine:init', () => {
    Alpine.data('interactiveMapWithAccordion', () => ({
        ...pinClustering(),
        map: null,
        zoom: 2,
        // read by the Map component's +/- buttons to disable them at the limits
        maxZoom: mapMaxZoom(),
        activeAccordionItemId: null,
        selectedRegion: null,
        selectedCountry: null,
        selectedCategory: null,

        init() {
            const mapImage = this.$root.querySelector(`.${classes.mapImageNode}`);
            const isMobile = window.innerWidth < 768;
            this.map = new SvgPanZoom(mapImage, {
                zoomEnabled: true,
                zoomScaleSensitivity: 0.5,
                controlIconsEnabled: false,
                dblClickZoomEnabled: false,
                mouseWheelZoomEnabled: false,
                fit: true,
                zoom: 2,
                center: 1,
                minZoom: 1,
                maxZoom: this.maxZoom,
            });

            if (isMobile) {
                // on mobile show only part of the map
                this.map.zoomIn();
                this.map.pan({
                    x: -20,
                    y: 0,
                });
            }
            this.zoom = this.map.getZoom();

            // support configuration via url parameters
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            this.selectedRegion = urlParams.get('map-region') || null;
            if (this.selectedRegion) {
                this.selectTailSelectValue(`.card-list__filter__select-region`, this.selectedRegion);
            }
            this.selectedCountry = urlParams.get('map-country') || null;
            if (this.selectedCountry) {
                this.selectTailSelectValue(`.card-list__filter__select-country`, this.selectedCountry);
            }
            this.selectedCategory = urlParams.get('map-category') || null;
            if (this.selectedCategory) {
                this.selectTailSelectValue(`.card-list__filter__select-category`, this.selectedCategory);
            }

            // InteractiveMapWithAccordion.Backend.js rewrites the non-reactive
            // data-transform when an editor moves a pin; nudging the zoom is what
            // makes every x-bind:transform re-read it.
            this.$root.addEventListener('imwa-force-re-render', () => {
                const zoom = this.zoom;
                this.zoom = zoom + 0.0001;
                setTimeout(() => {
                    this.zoom = this.map ? this.map.getZoom() : zoom;
                }, 1);
            });

            this.initPinClustering();
        },

        destroy() {
            this.destroyPinClustering();
        },

        selectTailSelectValue(selectSelector, value) {
            this.$root.querySelector(selectSelector).nextSibling.querySelector(`li[data-key="${value}"]`).click();
        },

        onRegionChange(event) {
            this.unsetAccordionItemId();
            this.selectedRegion = event.target.value;
            const countryTail = this.$refs.countrySelect.nextSibling;
            if (this.selectedRegion) {
                this.selectedCountry = null;
                // update the tail-select by clicking the first element
                countryTail.querySelector('.dropdown-option').click();
            }

            // hide the countries that don't belong to the selected region
            const showAllRegions = !this.selectedRegion;
            Array.from(countryTail.querySelectorAll('.dropdown-option')).map((el, index) => {
                const countryName = el.dataset.key;
                el.classList[index === 0 || showAllRegions || this.countryBelongsToRegion(this.selectedRegion, countryName) ? 'remove' : 'add']('is-hidden');
            });

            this.recalculatePinClusters();
            this.updateUrlParam('map-region', this.selectedRegion);
        },

        onCountryChange(event) {
            this.selectedCountry = event.target.value;
            this.unsetAccordionItemId();

            this.recalculatePinClusters();
            this.updateUrlParam('map-country', this.selectedCountry);
        },

        onCategoryChange(event) {
            this.selectedCategory = event.target.value;
            this.unsetAccordionItemId();

            this.recalculatePinClusters();
            this.updateUrlParam('map-category', this.selectedCategory);
        },

        updateUrlParam(name, value) {
            const url = new URL(window.location.href);
            url.searchParams.set(name, value);
            window.history.replaceState({}, '', url);
        },

        countryBelongsToRegion(regionCode, countryCode) {
            const regionCountries = {
                'africa': ['DZ'],
                'asia-and-pacific': ['AU', 'IN', 'NZ'],
                'europe-and-middle-east': ['AT', 'BY', 'BG', 'DE', 'GR', 'IE', 'LB', 'NL', 'NO', 'ES', 'SE', 'AE', 'GB'],
                'latin-america': ['AR', 'BR', 'CL', 'CO', 'DO', 'EC', 'PA', 'PE', 'MX'],
                'north-america': ['US', 'CA'],
            };
            return regionCountries[regionCode].includes(countryCode);
        },

        /**
         *
         * @param countryCodes array of strings
         * @param categoryNames string, comma separated
         * @returns {boolean}
         */
        isPinSelectable(countryCodes, categoryNames) {
            if (this.selectedCountry) {
                let match = false;
                countryCodes.forEach((countryCode) => {
                    if (this.selectedCountry === countryCode) {
                        match = true;
                    }
                });
                if (!match) {
                    return false;
                }
            }
            if (this.selectedRegion) {
                let match = false;
                countryCodes.forEach((countryCode) => {
                    if (this.countryBelongsToRegion(this.selectedRegion, countryCode)) {
                        match = true;
                    }
                });
                if (!match) {
                    return false;
                }
            }
            if (this.selectedCategory && !categoryNames.split(',').includes(this.selectedCategory)) {
                return false;
            }
            return true;
        },

        pinTranslatePosition($el) {
            let zoomFactor = Math.min(2 / this.zoom, 1);
            if (window.innerWidth < 768) { // pins need to be bigger on mobile
                zoomFactor = zoomFactor * 1.5;
            }
            const translate = $el.getAttribute('data-transform');
            const pinSize = JSON.parse($el.getAttribute('data-pin-size'));
            return `${translate} translate(${(pinSize.width / 2) * (1-zoomFactor)}, ${pinSize.height * (1-zoomFactor)}) scale(${zoomFactor})`;
        },

        toggleAccordionItem(activeAccordionItemId) {
            if (this.activeAccordionItemId && this.activeAccordionItemId === activeAccordionItemId) {
                this.unsetAccordionItemId();
            } else {
                this.activeAccordionItemId = activeAccordionItemId;

                // scroll on mobile
                if (window.innerWidth < 768) {
                    setTimeout(() => {
                        this.scrollToElement(document.getElementById(this.activeAccordionItemId));
                    }, 350);
                }
            }
        },

        openAndScrollToAccordionItem(activeAccordionItemId) {
            this.activeAccordionItemId = activeAccordionItemId;

            setTimeout(() => {
                this.scrollToElement(document.getElementById(this.activeAccordionItemId));
            }, 350);
        },

        scrollToElement(element) {
            const computedStyles = window.getComputedStyle(document.body);
            const switchLanguageNoticeHeight = parseInt(computedStyles.getPropertyValue('--switch-language-notice-height')) || 0;
            const headerHeight = parseInt(computedStyles.getPropertyValue('--header-height')) || 0;
            const globalOffset = switchLanguageNoticeHeight + headerHeight;

            window.scrollTo({
                behavior: 'smooth',
                left: 0,
                top: element.offsetTop + element.closest('.imwa__accordion').offsetTop - globalOffset - 15
            });
        },

        unsetAccordionItemId() {
            this.activeAccordionItemId = null;
        },

        animateMove(zoomAndPanCallback) {
            this.$root.classList.add(classes.mapAnimated);
            zoomAndPanCallback();
            this.zoom = this.map.getZoom();
            this.recalculatePinClusters();
            setTimeout(() => {
                this.$root.classList.remove(classes.mapAnimated);
            }, 300);
        },

        zoomIn() {
            this.animateMove(() => {
                this.map.zoomIn();
            });
        },

        zoomOut() {
            this.animateMove(() => {
                this.map.zoomOut();
            });
        },
    }));
});
